import { useEffect, useState } from 'react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import Fingerprint from '@/components/elements/Fingerprint';
import ModalBase, { ModalBasicProps } from '@/components/layouts/Modal/Base';
import Cc3D2Auth from '@/components/layouts/Modal/Order/Cc3D2Auth';
import QuickChargeFailure from '@/components/layouts/Modal/Order/QuickCharge/Failure';
import QuickChargeSuccess from '@/components/layouts/Modal/Order/QuickCharge/Success';
import QuickChargeTimeout from '@/components/layouts/Modal/Order/QuickCharge/Timeout';
import QuickChargeTop from '@/components/layouts/Modal/Order/QuickCharge/Top';
import { useUserContext } from '@/contexts/user';
import {
  CreditCard3d2AuthResponse,
  CreditCard3dAuthStatus,
  CreditCardMemberStatus,
  useChargeToretaFromCreditV2Mutation,
  useChargeToretaFromWalletMutation,
  useGetCreditCardStatusQuery,
  useGetUserWalletLazyQuery,
  User,
} from '@/lib/apollo/types/gen/graphql';
import { sendToretaChargeEvent } from '@/lib/dmm/trackingTag';
import { ChargeToretaLabel } from '@/lib/dmm/trackingTag/type';
import { ChargeType } from '@/modelTypes';
import { creditCardSelector } from '@/selectors/creditCardBrand';

import {
  ContentWrapper,
  DailyOrderLimitLinkArea,
  ExternalLink,
  Title,
} from './style';

export enum QuickChargeStep {
  Standby,
  InProgress,
  Succeed,
  Failed,
  Timeout,
}

interface QuickChargeModalProps extends ModalBasicProps {}

const QuickChargeModalContent: React.FC<QuickChargeModalProps> = ({
  handleClose,
}) => {
  const { setUser, user } = useUserContext();
  const { isPcc, isAvailable } = creditCardSelector(user.creditCard);

  const [quickChargeStep, setQuickChargeStep] = useState<QuickChargeStep>(
    QuickChargeStep.Standby,
  );
  const [chargeType, setChargeType] = useState<ChargeType>(
    isAvailable ? ChargeType.Credit : ChargeType.Wallet,
  );
  const [userDevice, setUserDevice] = useState<string>('');

  const [chargeToretaFromWalletMutation] = useChargeToretaFromWalletMutation();

  const creditCardStatus = useGetCreditCardStatusQuery({
    pollInterval: 10 * 1000,
  });

  // 3Dセキュア2.0
  const [chargeToretaFromCreditV2Mutation] =
    useChargeToretaFromCreditV2Mutation();
  const [auth2Response, setAuth2Response] =
    useState<CreditCard3d2AuthResponse | null>(null);

  const chargeToretaFromCreditV2 = (amount: number) => {
    chargeToretaFromCreditV2Mutation({
      variables: {
        amount,
      },
      context: {
        headers: {
          'X-User-Device': userDevice,
        },
      },
    })
      .then(res => {
        if (
          res.data &&
          res.data.chargeToretaFromCreditV2.result ===
            CreditCard3dAuthStatus.Approve
        ) {
          sendToretaChargeEvent(ChargeToretaLabel.QuickChargeFromCredit);
          setAuth2Response(res.data.chargeToretaFromCreditV2);
          setQuickChargeStep(QuickChargeStep.InProgress);
        }
      })
      .catch(err => {
        console.error(err);
        setQuickChargeStep(QuickChargeStep.Failed);
      });
  };

  const chargeToretaFromWallet = (amount: number) => {
    chargeToretaFromWalletMutation({
      variables: {
        amount,
      },
    })
      .then(res => {
        if (res.data) {
          sendToretaChargeEvent(ChargeToretaLabel.QuickChargeFromWallet);
          setQuickChargeStep(QuickChargeStep.Succeed);
        }
      })
      .catch(err => {
        console.error(err);
        setQuickChargeStep(QuickChargeStep.Failed);
      });
  };

  const [walletLQ, walletLQResult] = useGetUserWalletLazyQuery({
    fetchPolicy: 'network-only',
  });
  // チャージ成功時にウォレットを取得
  useEffect(() => {
    if (quickChargeStep === QuickChargeStep.Succeed) {
      // toreta+反映に若干ラグがあるため2秒待つ
      new Promise(resolve => setTimeout(resolve, 2000)).then(() => {
        walletLQ();
      });
    }
  }, [quickChargeStep]);
  useEffect(() => {
    if (
      walletLQResult.called &&
      !walletLQResult.loading &&
      walletLQResult.data
    ) {
      setUser(walletLQResult.data.user as User);
    }
  }, [walletLQResult.data]);

  return (
    <>
      <Title>
        toreta+ チャージ
        {quickChargeStep === QuickChargeStep.InProgress && '（処理中...）'}
        {quickChargeStep === QuickChargeStep.Timeout && '（タイムアウト）'}
      </Title>
      {user.dailyOrderLimit !== null && (
        <DailyOrderLimitLinkArea>
          <Link to="/my/limit" onClick={handleClose}>
            投票上限金額が設定されています
          </Link>
        </DailyOrderLimitLinkArea>
      )}
      {isAvailable && !isPcc && (
        <DailyOrderLimitLinkArea>
          <ExternalLink
            href={`${import.meta.env.VITE_DMM_NAV_BASE_URL}/news/kl3j480j_mh`}
            target="_blank"
          >
            【重要】クレジットカードメンテナンスによるVISAカード利用の一時停止について
          </ExternalLink>
        </DailyOrderLimitLinkArea>
      )}
      <ContentWrapper>
        {quickChargeStep === QuickChargeStep.Standby && (
          <QuickChargeTop
            chargeType={chargeType}
            creditCardStatus={
              (creditCardStatus.data?.creditCardStatus
                .memberStatus as CreditCardMemberStatus) ||
              CreditCardMemberStatus.Unknown
            }
            setChargeType={setChargeType}
            handleClose={() => handleClose()}
            chargeToretaFromCredit={amount => chargeToretaFromCreditV2(amount)}
            chargeToretaFromWallet={amount => chargeToretaFromWallet(amount)}
          />
        )}
        {quickChargeStep === QuickChargeStep.InProgress && (
          <>
            {auth2Response && (
              <Cc3D2Auth
                authResponse={auth2Response}
                onSuccess={() => setQuickChargeStep(QuickChargeStep.Succeed)}
                onFailure={() => setQuickChargeStep(QuickChargeStep.Failed)}
                onTimeout={() => setQuickChargeStep(QuickChargeStep.Timeout)}
              />
            )}
          </>
        )}
        {quickChargeStep === QuickChargeStep.Succeed && (
          <QuickChargeSuccess
            chargeType={chargeType}
            handleClose={() => handleClose()}
          />
        )}
        {quickChargeStep === QuickChargeStep.Failed && (
          <QuickChargeFailure
            chargeType={chargeType}
            handleClose={() => handleClose()}
          />
        )}
        {quickChargeStep === QuickChargeStep.Timeout && (
          <QuickChargeTimeout
            chargeType={chargeType}
            handleClose={() => handleClose()}
          />
        )}
        <Fingerprint setState={setUserDevice} />
      </ContentWrapper>
    </>
  );
};

const QuickChargeModal: React.FC<QuickChargeModalProps> = ({
  isOpen,
  handleClose,
}) => {
  return (
    <ModalBase isOpen={isOpen} handleClose={handleClose} desktopWidth="840px">
      <QuickChargeModalContent isOpen={isOpen} handleClose={handleClose} />
    </ModalBase>
  );
};

export default QuickChargeModal;
